import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Shield, Mail, Phone, Lock, CheckCircle, Loader2, ArrowRight, ShieldCheck, X } from 'lucide-react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SecurityPrompt({ user, isOpen, onClose, onUpdate }) {
  const [step, setStep] = useState('main');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [loading, setLoading] = useState(false);
  const [verifyingFor, setVerifyingFor] = useState(null);
  const [detectedCountry, setDetectedCountry] = useState('IN');

  // Password validation
  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(pwd)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(pwd)) errors.push('One lowercase letter');
    if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~]/.test(pwd)) errors.push('One special character');
    return errors;
  };

  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setPassword(pwd);
    setPasswordErrors(validatePassword(pwd));
    if (confirmPassword) {
      setPasswordsMatch(pwd === confirmPassword);
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const confirm = e.target.value;
    setConfirmPassword(confirm);
    setPasswordsMatch(password === confirm);
  };

  const isPasswordValid = passwordErrors.length === 0 && password.length >= 8;

  useEffect(() => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const countryMap = {
      'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN',
      'America/New_York': 'US', 'America/Los_Angeles': 'US',
      'Europe/London': 'GB', 'Asia/Dubai': 'AE',
    };
    if (countryMap[timezone]) setDetectedCountry(countryMap[timezone]);
  }, []);

  const hasEmail = user?.email && user.email.trim() !== '';
  const hasPhone = user?.phone && user.phone.trim() !== '';
  const hasPassword = user?.has_password;

  // Calculate security level
  const securityScore = [hasEmail, hasPhone, hasPassword].filter(Boolean).length;
  const securityLevel = securityScore === 0 ? 'Basic' : securityScore === 1 ? 'Medium' : securityScore === 2 ? 'Strong' : 'Maximum';
  const securityColor = securityScore === 0 ? '#EF4444' : securityScore === 1 ? '#F59E0B' : securityScore === 2 ? '#22D3EE' : '#22C55E';

  const sendOTP = async (type) => {
    const value = type === 'email' ? email : phone;
    if (!value) {
      toast.error(`Please enter your ${type === 'email' ? 'email address' : 'phone number'}`);
      return;
    }

    setLoading(true);
    try {
      const payload = type === 'email' 
        ? { email: value, method: 'email' }
        : { phone: value, method: 'sms' };
      
      await axios.post(`${API}/auth/send-otp`, payload);
      toast.success(`Verification code sent!`);
      setVerifyingFor(type);
      setStep('verify-otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = verifyingFor === 'email'
        ? { email, code: otp }
        : { phone, code: otp };
      
      await axios.post(`${API}/auth/verify-secondary`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${verifyingFor === 'email' ? 'Email' : 'Phone'} verified!`);
      onUpdate?.();
      setStep('main');
      setOtp('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const setUserPassword = async () => {
    if (!isPasswordValid) {
      toast.error('Please meet all password requirements');
      return;
    }
    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/set-password`, { password }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Password set successfully!');
      onUpdate?.();
      setStep('main');
      setPassword('');
      setConfirmPassword('');
      setPasswordErrors([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  const skipForNow = () => {
    localStorage.setItem('securityPromptDismissed', Date.now().toString());
    onClose();
  };

  const renderMain = () => (
    <div className="space-y-5">
      {/* Security Level Indicator */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'rgba(255,255,255,0.05)' }}>
          <ShieldCheck className="h-4 w-4" style={{ color: securityColor }} />
          <span className="text-sm font-medium" style={{ color: securityColor }}>
            Security Level: {securityLevel}
          </span>
        </div>
        
        {/* Progress bar */}
        <div className="mt-3 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
          <div 
            className="h-full rounded-full transition-all duration-500"
            style={{ 
              width: `${(securityScore / 3) * 100}%`,
              background: `linear-gradient(90deg, ${securityColor}, ${securityColor}88)`
            }}
          />
        </div>
      </div>

      {/* Options */}
      <div className="space-y-3">
        {/* Add Email */}
        {!hasEmail ? (
          <button
            onClick={() => setStep('add-email')}
            className="w-full p-4 rounded-xl flex items-center gap-4 transition-all hover:scale-[1.02]"
            style={{ background: 'rgba(124, 58, 237, 0.1)', border: '1px solid rgba(124, 58, 237, 0.2)' }}
          >
            <div className="p-2.5 rounded-lg" style={{ background: 'rgba(124, 58, 237, 0.2)' }}>
              <Mail className="h-5 w-5" style={{ color: '#A78BFA' }} />
            </div>
            <div className="flex-1 text-left">
              <p className="font-semibold text-sm" style={{ color: '#E5E7EB' }}>Add Email</p>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
                Backup access • Secure login • Security alerts
              </p>
            </div>
            <ArrowRight className="h-4 w-4" style={{ color: 'rgba(229, 231, 235, 0.4)' }} />
          </button>
        ) : (
          <div className="p-4 rounded-xl flex items-center gap-4" style={{ background: 'rgba(34, 197, 94, 0.08)' }}>
            <div className="p-2.5 rounded-lg" style={{ background: 'rgba(34, 197, 94, 0.2)' }}>
              <CheckCircle className="h-5 w-5 text-green-500" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm text-green-400">Email Verified</p>
              <p className="text-xs mt-0.5 truncate" style={{ color: 'rgba(34, 197, 94, 0.7)' }}>{user.email}</p>
            </div>
          </div>
        )}

        {/* Add Phone */}
        {!hasPhone ? (
          <button
            onClick={() => setStep('add-phone')}
            className="w-full p-4 rounded-xl flex items-center gap-4 transition-all hover:scale-[1.02]"
            style={{ background: 'rgba(34, 211, 238, 0.1)', border: '1px solid rgba(34, 211, 238, 0.2)' }}
          >
            <div className="p-2.5 rounded-lg" style={{ background: 'rgba(34, 211, 238, 0.2)' }}>
              <Phone className="h-5 w-5" style={{ color: '#22D3EE' }} />
            </div>
            <div className="flex-1 text-left">
              <p className="font-semibold text-sm" style={{ color: '#E5E7EB' }}>Add Phone</p>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
                Fastest access • Instant OTP • Stay connected
              </p>
            </div>
            <ArrowRight className="h-4 w-4" style={{ color: 'rgba(229, 231, 235, 0.4)' }} />
          </button>
        ) : (
          <div className="p-4 rounded-xl flex items-center gap-4" style={{ background: 'rgba(34, 197, 94, 0.08)' }}>
            <div className="p-2.5 rounded-lg" style={{ background: 'rgba(34, 197, 94, 0.2)' }}>
              <CheckCircle className="h-5 w-5 text-green-500" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm text-green-400">Phone Verified</p>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(34, 197, 94, 0.7)' }}>{user.phone}</p>
            </div>
          </div>
        )}

        {/* Set Password */}
        {!hasPassword ? (
          <button
            onClick={() => setStep('set-password')}
            className="w-full p-4 rounded-xl flex items-center gap-4 transition-all hover:scale-[1.02]"
            style={{ background: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251, 191, 36, 0.2)' }}
          >
            <div className="p-2.5 rounded-lg" style={{ background: 'rgba(251, 191, 36, 0.2)' }}>
              <Lock className="h-5 w-5" style={{ color: '#FBBF24' }} />
            </div>
            <div className="flex-1 text-left">
              <p className="font-semibold text-sm" style={{ color: '#E5E7EB' }}>Set Password</p>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
                No OTP needed • Instant login • Minimal trace
              </p>
            </div>
            <ArrowRight className="h-4 w-4" style={{ color: 'rgba(229, 231, 235, 0.4)' }} />
          </button>
        ) : (
          <div className="p-4 rounded-xl flex items-center gap-4" style={{ background: 'rgba(34, 197, 94, 0.08)' }}>
            <div className="p-2.5 rounded-lg" style={{ background: 'rgba(34, 197, 94, 0.2)' }}>
              <CheckCircle className="h-5 w-5 text-green-500" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm text-green-400">Password Set</p>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(34, 197, 94, 0.7)' }}>Username login enabled</p>
            </div>
          </div>
        )}
      </div>

      {/* Action buttons */}
      {securityScore < 3 && (
        <button 
          onClick={skipForNow}
          className="w-full text-sm py-2 transition-colors hover:text-white"
          style={{ color: 'rgba(229, 231, 235, 0.4)' }}
        >
          I'll do this later
        </button>
      )}

      {securityScore === 3 && (
        <Button 
          onClick={onClose}
          className="w-full h-11 text-white font-semibold"
          style={{ background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)' }}
        >
          <CheckCircle className="h-4 w-4 mr-2" />
          Account Fully Secured
        </Button>
      )}
    </div>
  );

  const renderAddEmail = () => (
    <div className="space-y-5">
      <div 
        className="p-4 rounded-xl"
        style={{ background: 'rgba(124, 58, 237, 0.08)', border: '1px solid rgba(124, 58, 237, 0.15)' }}
      >
        <div className="flex items-center gap-2 mb-3">
          <Mail className="h-4 w-4" style={{ color: '#A78BFA' }} />
          <p className="text-sm font-semibold" style={{ color: '#A78BFA' }}>Add Email</p>
        </div>
        <ul className="text-xs space-y-1.5" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
          <li>• Backup access if your phone is unavailable</li>
          <li>• Secure login from any device</li>
          <li>• Important security notifications</li>
        </ul>
      </div>

      <div className="space-y-2">
        <Input
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl"
        />
      </div>

      <Button 
        onClick={() => sendOTP('email')}
        className="w-full h-11 text-white font-semibold rounded-xl"
        style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)' }}
        disabled={loading || !email}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify Email'}
      </Button>

      <button 
        onClick={() => setStep('main')}
        className="w-full text-sm py-2 transition-colors hover:text-white"
        style={{ color: 'rgba(229, 231, 235, 0.4)' }}
      >
        Back
      </button>
    </div>
  );

  const renderAddPhone = () => (
    <div className="space-y-5">
      <div 
        className="p-4 rounded-xl"
        style={{ background: 'rgba(34, 211, 238, 0.08)', border: '1px solid rgba(34, 211, 238, 0.15)' }}
      >
        <div className="flex items-center gap-2 mb-3">
          <Phone className="h-4 w-4" style={{ color: '#22D3EE' }} />
          <p className="text-sm font-semibold" style={{ color: '#22D3EE' }}>Add Phone</p>
        </div>
        <ul className="text-xs space-y-1.5" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
          <li>• Fastest way to access your account</li>
          <li>• Instant OTP login anytime</li>
          <li>• Stay connected without interruption</li>
        </ul>
      </div>

      <PhoneInput
        international
        defaultCountry={detectedCountry}
        value={phone}
        onChange={setPhone}
        className="phone-input-dark"
      />

      <Button 
        onClick={() => sendOTP('phone')}
        className="w-full h-11 text-white font-semibold rounded-xl"
        style={{ background: 'linear-gradient(135deg, #06B6D4 0%, #22D3EE 100%)' }}
        disabled={loading || !phone}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify Phone'}
      </Button>

      <button 
        onClick={() => setStep('main')}
        className="w-full text-sm py-2 transition-colors hover:text-white"
        style={{ color: 'rgba(229, 231, 235, 0.4)' }}
      >
        Back
      </button>
    </div>
  );

  const renderVerifyOTP = () => (
    <div className="space-y-5">
      <div className="text-center">
        <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
          Enter the 6-digit code sent to
        </p>
        <p className="font-semibold mt-1 truncate" style={{ color: '#E5E7EB' }}>
          {verifyingFor === 'email' ? email : phone}
        </p>
      </div>

      <Input
        type="text"
        inputMode="numeric"
        placeholder="000000"
        value={otp}
        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
        maxLength={6}
        className="bg-white/5 border-white/10 text-white text-center text-2xl tracking-[0.5em] h-14 rounded-xl"
      />

      <Button 
        onClick={verifyOTP}
        className="w-full h-11 text-white font-semibold rounded-xl"
        style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
        disabled={loading || otp.length !== 6}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
      </Button>

      <button 
        onClick={() => { setStep(verifyingFor === 'email' ? 'add-email' : 'add-phone'); setOtp(''); }}
        className="w-full text-sm py-2 transition-colors hover:text-white"
        style={{ color: 'rgba(229, 231, 235, 0.4)' }}
      >
        Change {verifyingFor}
      </button>
    </div>
  );

  const renderSetPassword = () => (
    <div className="space-y-5">
      <div 
        className="p-4 rounded-xl"
        style={{ background: 'rgba(251, 191, 36, 0.08)', border: '1px solid rgba(251, 191, 36, 0.15)' }}
      >
        <div className="flex items-center gap-2 mb-3">
          <Lock className="h-4 w-4" style={{ color: '#FBBF24' }} />
          <p className="text-sm font-semibold" style={{ color: '#FBBF24' }}>Set Password</p>
        </div>
        <ul className="text-xs space-y-1.5" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
          <li>• Instant login with username</li>
          <li>• No OTP required</li>
          <li>• Maximum privacy, minimal trace</li>
        </ul>
      </div>

      <div className="space-y-3">
        <div>
          <Input
            type="password"
            placeholder="Create password"
            value={password}
            onChange={handlePasswordChange}
            className={`bg-white/5 text-white placeholder:text-gray-500 h-12 rounded-xl ${
              password && !isPasswordValid ? 'border-red-500' : 'border-white/10'
            }`}
          />
          {/* Password requirements */}
          {password && (
            <div className="mt-2 space-y-1">
              {[
                { text: 'At least 8 characters', valid: password.length >= 8 },
                { text: 'One uppercase letter', valid: /[A-Z]/.test(password) },
                { text: 'One lowercase letter', valid: /[a-z]/.test(password) },
                { text: 'One special character', valid: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~]/.test(password) },
              ].map((req, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs">
                  {req.valid ? (
                    <CheckCircle className="h-3 w-3 text-green-500" />
                  ) : (
                    <div className="w-3 h-3 rounded-full border border-gray-500" />
                  )}
                  <span style={{ color: req.valid ? '#22C55E' : 'rgba(229,231,235,0.5)' }}>{req.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div>
          <Input
            type="password"
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            className={`bg-white/5 text-white placeholder:text-gray-500 h-12 rounded-xl ${
              confirmPassword && !passwordsMatch ? 'border-red-500' : 'border-white/10'
            }`}
          />
          {confirmPassword && !passwordsMatch && (
            <p className="text-xs mt-1 text-red-500">Passwords do not match</p>
          )}
        </div>
      </div>

      <Button 
        onClick={setUserPassword}
        className="w-full h-11 text-white font-semibold rounded-xl"
        style={{ background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)' }}
        disabled={loading || !isPasswordValid || !passwordsMatch}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set Password'}
      </Button>

      <button 
        onClick={() => { setStep('main'); setPassword(''); setConfirmPassword(''); setPasswordErrors([]); }}
        className="w-full text-sm py-2 transition-colors hover:text-white"
        style={{ color: 'rgba(229, 231, 235, 0.4)' }}
      >
        Back
      </button>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="border-0 max-w-sm p-0 overflow-hidden"
        style={{ 
          background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.98) 0%, rgba(10, 15, 30, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.08)'
        }}
      >
        <DialogTitle className="sr-only">Security Settings</DialogTitle>
        
        {/* Close button for mobile */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 transition-colors z-10"
          style={{ color: 'rgba(229, 231, 235, 0.5)' }}
          data-testid="security-modal-close"
        >
          <X className="h-5 w-5" />
        </button>
        
        {/* Header */}
        <div className="p-6 pb-4 text-center" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <div 
            className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
          >
            <Shield className="h-7 w-7 text-white" />
          </div>
          <h2 className="text-lg font-bold" style={{ color: '#E5E7EB' }}>
            Strengthen Your Private Access
          </h2>
          <p className="text-xs mt-1" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
            Secure your account for uninterrupted and private access
          </p>
        </div>

        {/* Content */}
        <div className="p-6 pt-5">
          {step === 'main' && renderMain()}
          {step === 'add-email' && renderAddEmail()}
          {step === 'add-phone' && renderAddPhone()}
          {step === 'verify-otp' && renderVerifyOTP()}
          {step === 'set-password' && renderSetPassword()}
        </div>

        {/* Phone input styles */}
        <style>{`
          .phone-input-dark .PhoneInputInput {
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 0.75rem;
            padding: 0.75rem 1rem;
            color: white;
            font-size: 16px;
            height: 48px;
          }
          .phone-input-dark .PhoneInputInput:focus {
            border-color: #22D3EE;
            outline: none;
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}
